/**
 * Pure-logic helpers for the magic-paste billing-codes EHR actions.
 * Tested in src/utils/__tests__/billingCodesPaste.test.js.
 * Consumed by src/hooks/useEHR.js (React-side dispatcher).
 */

import { extractLosCode, isCodeValidForLosSlot, isPlausibleCode } from "./losCodeParse";
import { isNonBillableList, isNotBilled } from "./notBilled";

export const NON_BILLABLE_SKIP_REASON = "nonBillableSection";

// Matches only the trailing suffix the feature writes: "(not billed)" or
// "(patient-supplied, not billed)".
const NOT_BILLED_TEXT = /\((?:[^()]*,\s*)?not billed\)\s*$/i;

// The two levelOfService code slots get a recovery attempt when their token isn't
// code-shaped: the backend sometimes writes LOS as prose ("Annual Wellness Code:
// 99386, E/M Code: 99213, Modifier: 25") which has no " - " to split — before this
// existed, the WHOLE sentence was typed into AdvancedMD's CPT search (code-audit
// 2026-07-15). Other slots get no guesswork: garbage drops visibly via invalidTokens.
const LOS_SLOT_FAMILY = { enmCode: "992", annualCode: "993" };

export function sanitizeCodes(source, codeProperty, opts = {}) {
  const dropped = { empty: 0, duplicate: 0, wrongSlot: 0, invalidTokens: [] };
  const out = [];
  // opts.honorNotBilled === false (ICD-10 lists, which are never "billed" per se) ignores
  // the object flag and the text suffix entirely.
  const honorNotBilled = opts.honorNotBilled !== false;

  if (source == null) return { codes: out, dropped };

  // A step naming the non-billable list pastes nothing, flagged rows or not (design
  // 2026-10-22). This only fires when the caller passes opts.valueSource; useEHR does so at
  // every call site (guarded by useEHR.slipValueSource.test.js), including the
  // honorNotBilled:false pasteAndPickList path. Its codes are reported through the
  // not-billed keys so the sync report lists them as left off the charge slip.
  if (isNonBillableList(opts.valueSource)) {
    const { codes } = sanitizeCodes(source, codeProperty, { ...opts, valueSource: undefined, honorNotBilled: false });
    dropped.skipReason = NON_BILLABLE_SKIP_REASON;
    if (codes.length) {
      dropped.notBilled = codes.length;
      dropped.notBilledCodes = codes;
    }
    return { codes: out, dropped };
  }

  // Three input shapes from the backend:
  //  - Array of objects: [{ code: "Z79.01", description: "..." }, ...]   (legacy "raw" list)
  //  - Array of strings: ["3075F - Systolic 130-139 mmHg", ...]           (AI agent writer)
  //  - Object keyed by "<code> - <description>": { "N52.9 - Male ED": {evidence, specificity, alternatives, v28}, ... }
  //    (newer scored-ICD-10 evidence format; same shape `normalizList` in utils.js
  //    already handles for the chat-side render path)
  // For the dictionary shape, treat each key as a string item — the per-item branch
  // below will extract the code by splitting on " - ".
  let raw;
  const flaggedKeys = new Set();
  if (opts.treatAsSingle) {
    raw = [source];
  } else if (Array.isArray(source)) {
    raw = source;
  } else if (typeof source === "object") {
    raw = Object.keys(source);
    // A dictionary value can carry the flag too; remember which keys are flagged.
    if (honorNotBilled) for (const key of raw) if (isNotBilled(source[key])) flaggedKeys.add(key);
  } else {
    raw = [];
  }

  // Only the two levelOfService slots are family-constrained. cpt2codes /
  // inOfficeProcedures legitimately carry 992xx and 993xx CPTs, so they get no
  // slot check — undefined here disables it.
  const slotFamily = opts.treatAsSingle ? LOS_SLOT_FAMILY[codeProperty] : undefined;

  const seen = new Set();
  const seenInvalid = new Set();
  // The not-billed keys are added only when a line is skipped, so a list without the
  // flag yields the exact legacy drop report.
  // notBilledCodes is finalized after the loop: a flagged copy of a code that also has a
  // billed copy in the same list is not "left off the charge slip", so it is not reported.
  const flaggedCodes = [];
  const skipNotBilled = (code) => {
    dropped.notBilled = (dropped.notBilled || 0) + 1;
    if (code) flaggedCodes.push(code);
  };
  for (const item of raw) {
    let v;
    let cleaned = null;
    if (typeof item === "string") {
      // Some writers (AI agent for cpt2codes, LOS slot) store items as plain
      // strings in "<code> - <description>" form, or as the bare code itself.
      // The LOS Annual prompt also wraps its CPT output in <p>...</p> tags,
      // and other LOS prompts use similar HTML decorators — strip those
      // before the dash split so AdvancedMD's picker receives a bare code.
      cleaned = item.replace(/<[^>]*>/g, "");
      const dashIdx = cleaned.indexOf(" - ");
      v = dashIdx >= 0 ? cleaned.slice(0, dashIdx) : cleaned;
    } else {
      v = item && item[codeProperty];
    }
    let trimmed = typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
    // A string entry cannot carry the flag, so its text is the only signal: a model that
    // wrote "(patient-supplied, not billed)" into a string line must under-bill visibly in
    // the sync report rather than have the drug pasted to the slip. The text check is
    // opt-in (opts.notBilledText, CPT lists only) and anchored to the trailing suffix the
    // feature writes, so ICD-10 keys and "not billed separately" descriptions still paste.
    if (
      honorNotBilled &&
      (isNotBilled(item) ||
        (typeof item === "string" && flaggedKeys.has(item)) ||
        (cleaned != null && opts.notBilledText && NOT_BILLED_TEXT.test(cleaned)))
    ) {
      skipNotBilled(trimmed);
      continue;
    }
    // A code token that stringifies to "undefined"/"null" is an upstream data defect —
    // an entry whose code field was lost, so a "<code> - <description>" key/string was
    // built as "undefined - <description>". No real ICD-10/CPT code is ever these words,
    // so drop it at the EHR boundary (counts as empty) rather than pasting "undefined"
    // into AdvancedMD's picker. Only the code token is checked, never the description.
    const lower = trimmed.toLowerCase();
    if (!trimmed || lower === "undefined" || lower === "null") {
      dropped.empty++;
      continue;
    }
    // Anything that isn't a plausible code shape (digit-jams like "993869921325",
    // prose sentences, modifier-suffixed strings) would silently match nothing in
    // an EHR picker. For the two LOS slots, first try to recover the family code
    // from the full string (the prose shape carries a real code); otherwise drop
    // the token VISIBLY — deduped, so a repeated bad value doesn't spam the
    // missed-codes toast N times.
    if (!isPlausibleCode(trimmed)) {
      const recovered = slotFamily ? extractLosCode(cleaned ?? trimmed, slotFamily) : "";
      if (!recovered) {
        if (!seenInvalid.has(trimmed)) {
          seenInvalid.add(trimmed);
          dropped.invalidTokens.push(trimmed);
        }
        continue;
      }
      trimmed = recovered;
    }
    // Last line of defense before the EHR picker. An encounter poisoned before the
    // parse fix carries a code-SHAPED cross-slot value (annualCode "99204"), which
    // healLosObject only rewrites once the LOS component mounts AND the user touches
    // a control — a sync can reach here with it intact. Pasting it adds a second E/M
    // line to the AdvancedMD charge slip (field report 2026-09-22). Counted, not
    // pushed to invalidTokens: the code is not missing from the chart, it is a copy
    // of the other slot's code that was never valid here, so the missed-codes toast
    // would misreport it as a code we failed to enter.
    if (slotFamily && !isCodeValidForLosSlot(trimmed, slotFamily)) {
      dropped.wrongSlot++;
      continue;
    }
    if (seen.has(trimmed)) {
      dropped.duplicate++;
      continue;
    }
    seen.add(trimmed);
    out.push(trimmed);
  }
  if (dropped.notBilled) {
    dropped.notBilledCodes = [...new Set(flaggedCodes)].filter((c) => !seen.has(c));
  }
  return { codes: out, dropped };
}

// A not-billed line is reported as skipped, not as a code the sync failed to enter.
export const NOT_BILLED_MISS_REASON = "not_billed";

export function notBilledMissEntries(dropped, type) {
  return (dropped?.notBilledCodes || []).map((code) => ({ type, code, reason: NOT_BILLED_MISS_REASON }));
}

// Entries to add to the sync report for a not-billed skip, minus any already reported for
// the same code+list (the paste step and the fixed-value charge-slip step both sanitize
// the same list, so without this a skipped code would be reported twice).
export function newNotBilledMissEntries(existing, dropped, type) {
  const have = new Set(
    (existing || []).filter((m) => m.reason === NOT_BILLED_MISS_REASON).map((m) => m.type + "\u0000" + m.code)
  );
  return notBilledMissEntries(dropped, type).filter((m) => !have.has(m.type + "\u0000" + m.code));
}

// Same labels the pasteSearchPick missed-code entries use, so one code on one list gets
// one label wherever it is reported.
export function codeListLabel(valueSource) {
  if (valueSource === "cpt2codes") return "CPT";
  if (valueSource === "inOfficeProcedures") return "OP";
  if (valueSource === "nonBillableInjections") return "NBI";
  if (valueSource === "levelOfService") return "LOS";
  return valueSource;
}

// True when the sync report holds anything beyond informational not-billed skips. Skips
// alone must not suppress the "sent to the EHR" success toast.
export function hasActionableMisses(entries) {
  return (entries || []).some((m) => m && m.reason !== NOT_BILLED_MISS_REASON);
}

export function formatNotBilledNotice(entries) {
  const list = (entries || []).map((m) => (m.type ? m.type + " " : "") + m.code).join(", ");
  return "Skipped (not billed), left off the charge slip: " + list;
}

export function buildPasteString(codes, separator, trailingSeparator) {
  if (!Array.isArray(codes) || codes.length === 0) return "";
  return codes.join(separator) + (trailingSeparator || "");
}

/**
 * Escape a string as an XPath 1.0 literal. XPath has no escape syntax for
 * quotes — if the string contains both ' and ", we must use concat() to
 * splice them. ICD-10 codes never contain quotes, but descriptions could,
 * and a future caller might pass description-bearing text.
 */
export function escapeXPathLiteral(str) {
  if (typeof str !== "string") str = String(str ?? "");
  const hasSingle = str.includes("'");
  const hasDouble = str.includes('"');
  if (!hasSingle) return `'${str}'`;
  if (!hasDouble) return `"${str}"`;
  // Both present — split on ' and rebuild with concat()
  const parts = str.split("'");
  const args = [];
  parts.forEach((p, i) => {
    if (p.length > 0) args.push(`'${p}'`);
    if (i < parts.length - 1) args.push(`"'"`);
  });
  return `concat(${args.join(", ")})`;
}

/**
 * Substitute each code into the selector template. Two placeholders supported:
 *   ${code}         — raw substitution (use when the surrounding XPath already quotes)
 *   ${code_literal} — XPath-literal-escaped substitution with a trailing space
 *                     (recommended; the trailing space delimits "Z79.01 " from "Z79.011")
 */
export function buildIcd10OptionSelectors(codes, template) {
  if (!Array.isArray(codes) || codes.length === 0) return [];
  return codes.map((code) => {
    const literal = escapeXPathLiteral(code + " ");
    return template.replace(/\$\{code_literal\}/g, literal).replace(/\$\{code\}/g, code);
  });
}

/**
 * Stable-partition `codes` so those starting with `prefix` (case-insensitive) come
 * first, preserving the relative order within each group. Lets a template prioritize
 * a code family in the paste/pick order — e.g. `prioritizePrefix: "Z"` to list Z-codes
 * first on the charge slip. Returns a NEW array (never mutates the input); returns the
 * codes unchanged when `prefix` is blank.
 */
export function prioritizeByPrefix(codes, prefix) {
  if (!Array.isArray(codes)) return [];
  if (!prefix) return [...codes];
  const p = String(prefix).toUpperCase();
  const first = [];
  const rest = [];
  for (const code of codes) {
    if (String(code).toUpperCase().startsWith(p)) first.push(code);
    else rest.push(code);
  }
  return [...first, ...rest];
}

// EHR systems whose templates may use the pasteAndPickList action. The webapp
// gate (useEHR.js) and the per-system extension implementations (background.js)
// must stay in lockstep with this list — the coupling is MANUAL (there is no shared
// build between the two bundles). Adding a system here without a matching
// background.js dispatch branch does NOT ship a no-op that reports every code
// missed: background.js's dispatch is now explicit (SF-1) and returns
// {error: "unsupported_ehr_system", missed: [...]} for any ehrSystem it doesn't
// recognize, which useEHR.js surfaces via the normal missed-codes toast.
export const PASTE_AND_PICK_LIST_SYSTEMS = ["advancedmd", "practicefusion"];

export function isPasteAndPickListSupported(system) {
  return PASTE_AND_PICK_LIST_SYSTEMS.includes(system);
}

/**
 * Build the optional ground-truth verifySelectors for pasteAndPickList, or null when
 * no verifySelectorTemplate is configured. Guards on `typeof template !== "string"`
 * (not mere truthiness) so a mis-authored non-string verifySelectorTemplate returns
 * null instead of reaching buildIcd10OptionSelectors's `template.replace(...)` call,
 * which would throw. Mirrors chargeSlipMapping.js's deriveSlipRowSelector null-guard
 * shape. Extracted from useEHR.js's inline ternary so this is unit-testable.
 */
export function buildVerifySelectors(codes, template) {
  if (typeof template !== "string") return null;
  return buildIcd10OptionSelectors(codes, template);
}

// Substrings that, anywhere in an ICD-10 code, make AdvancedMD's ICD picker abandon
// the per-code lookup for the whole comma-pasted list and run a description search
// instead — after which NONE of the pasted codes render and every probe misses.
// Field-reported 2026-09-24 alongside the dot-less case below (J00 in a five-code list
// hid all five; the same list without J00 rendered every code). Add a new substring
// here when the field reports another code that breaks the list; the match is
// case-insensitive and a false positive only costs speed (the code is pasted on its
// own instead of in the batch). Templates can extend this per org without a deploy
// via the action's `batchUnsafePatterns` array.
export const BATCH_UNSAFE_CODE_PATTERNS = ["A1", "B2"];

/**
 * True when `code` must NOT go into the AdvancedMD comma-paste batch. Three rules:
 *   - no decimal point (3-character codes: J00, I10, Z23, ...)
 *   - a letter immediately after the dot (the FY2024 Parkinson's family G20.A1, G20.A2,
 *     G20.B1, G20.B2, G20.C). A letter AFTER digits is fine — 7th-character and
 *     X-placeholder injury codes (S52.501A, T36.1X5A) ride the batch as always.
 *   - contains any of BATCH_UNSAFE_CODE_PATTERNS or `extraPatterns` (case-insensitive)
 * A non-string code is unsafe by definition — solo is the side that can't take a
 * good batch down with it.
 */
export function isBatchUnsafeCode(code, extraPatterns) {
  if (typeof code !== "string") return true;
  if (!code.includes(".")) return true;
  if (/\.[A-Z]/i.test(code)) return true;
  const upper = code.toUpperCase();
  const extras = Array.isArray(extraPatterns) ? extraPatterns : [];
  for (const pattern of [...BATCH_UNSAFE_CODE_PATTERNS, ...extras]) {
    if (typeof pattern !== "string") continue;
    const p = pattern.trim().toUpperCase();
    if (p && upper.includes(p)) return true;
  }
  return false;
}

/**
 * XPath that matches the first node matched by ANY of `selectors` — a plain XPath 1.0
 * union, so the shipped extension's document.evaluate accepts it unchanged. A single
 * selector is returned as-is; an empty list yields undefined (caller sends nothing).
 */
export function buildAnyOfSelector(selectors) {
  if (!Array.isArray(selectors) || selectors.length === 0) return undefined;
  if (selectors.length === 1) return selectors[0];
  return "(" + selectors.join(" | ") + ")[1]";
}

/**
 * Turn the ordered code list into the ordered list of extension requests useEHR.js
 * sends for one pasteAndPickList action. The note's order is preserved: each run of
 * consecutive batch-safe codes travels together in one comma-paste request (the fast
 * path, today's behavior), and each unsafe code gets its own request at its original
 * position, whose paste string is the bare code — no separator at all, because
 * "J00, " hid even J00 in the field while "J00" rendered it. So a first-listed I10
 * stays the first diagnosis on the claim, and a prioritized Z-code keeps its front
 * position; an unsafe code between safe ones simply splits the batch in two.
 *
 * Each entry carries everything the per-request payload needs to stay aligned
 * (codes ↔ optionSelectors ↔ verifySelectors are index-matched within an entry).
 * `mode` is "batch" | "solo" for logging. An empty code list yields an empty plan.
 */
export function planPasteAndPickListRequests(codes, action) {
  const a = action || {};
  const entry = (mode, list, pasteString) => {
    const optionSelectors = buildIcd10OptionSelectors(list, a.optionSelectorTemplate);
    const e = {
      mode,
      codes: list,
      pasteString,
      optionSelectors,
      verifySelectors: buildVerifySelectors(list, a.verifySelectorTemplate),
    };
    // Batch readiness = "any of OUR rows rendered". The shipped extension otherwise
    // waits for the FIRST code's row, so a non-billable parent (M54.5) or a code
    // AdvancedMD doesn't know at the front of the list would time the whole batch
    // out and drag every code into the extension's slow per-code fallback. Junk rows
    // from a description search still don't satisfy it. A solo request sends none:
    // the extension then waits for that code's own row, which is exactly right.
    if (mode === "batch") e.dropdownReadySelector = buildAnyOfSelector(optionSelectors);
    return e;
  };
  const plan = [];
  if (!Array.isArray(codes)) return plan;
  let run = [];
  const flushRun = () => {
    if (run.length === 0) return;
    plan.push(entry("batch", run, buildPasteString(run, a.separator || ", ", a.trailingSeparator || ", ")));
    run = [];
  };
  for (const code of codes) {
    if (isBatchUnsafeCode(code, a.batchUnsafePatterns)) {
      flushRun();
      plan.push(entry("solo", [code], code));
    } else {
      run.push(code);
    }
  }
  flushRun();
  return plan;
}

// Codes a reply positively reported on, either directly or as the requested parent of
// an auto-mapped child (M54.5 → M54.50 reports code M54.50 with originalCode M54.5).
function accountedCodes(reply) {
  const out = new Set();
  for (const m of [...(reply?.matched || []), ...(reply?.missed || [])]) {
    if (m?.code) out.add(m.code);
    if (m?.originalCode) out.add(m.originalCode);
  }
  return out;
}

/**
 * Run one pasteAndPickList action's plan against the extension: one request per plan
 * entry, in order, replies merged into the single {matched, missed, cancelled, error?}
 * shape useEHR.js already reports from. `sendRequest(payload)` is injected (the hook
 * passes lucasIntegrationRequest) so this is unit-testable without an extension, the
 * same way pfPickerDriver is. Never throws.
 *
 * Stop rules — each exists because the plan is now several requests where it used to
 * be one, and a partial run must stay honest:
 *   - a reply carrying a whole-run `error` (debugger attach refused, no EHR tab, …) or a
 *     sendRequest that throws ends the run; the first error is kept and the results
 *     merged so far are returned intact. The hook's unaccounted-codes sweep then marks
 *     every code no reply covered, so nothing is re-run just to fail the same way.
 *   - a `cancelled` reply, or `isCancelled()` turning true between requests, ends the
 *     run with every not-yet-reported code recorded as missed ("cancelled") so they
 *     reach the missed-codes toast instead of vanishing.
 */
export async function dispatchPasteAndPickListPlan({ plan, sendRequest, baseRequest, isCancelled, retryUnattempted }) {
  const result = { matched: [], missed: [], cancelled: false };
  const entries = Array.isArray(plan) ? plan : [];
  const cancelledNow = () => typeof isCancelled === "function" && isCancelled();
  const markCancelled = (fromIndex, alreadyAccounted) => {
    result.cancelled = true;
    entries.slice(fromIndex).forEach((e, i) => {
      for (const code of e.codes || []) {
        if (i === 0 && alreadyAccounted && alreadyAccounted.has(code)) continue;
        result.missed.push({ code, reason: "cancelled" });
      }
    });
  };
  const send = (fields) => sendRequest({ ...(baseRequest || {}), ...fields });

  for (let i = 0; i < entries.length; i++) {
    if (cancelledNow()) {
      markCancelled(i, null);
      break;
    }
    const req = entries[i];
    let reply;
    try {
      reply = await send({
        pasteString: req.pasteString,
        codes: req.codes,
        optionSelectors: req.optionSelectors,
        verifySelectors: req.verifySelectors,
        // Only a batch entry carries one (see planPasteAndPickListRequests); the shipped
        // extension falls back to the first option selector when the key is absent.
        ...(req.dropdownReadySelector ? { dropdownReadySelector: req.dropdownReadySelector } : {}),
      });
    } catch (e) {
      result.error = (e && e.message) || String(e);
      break;
    }
    result.matched.push(...(reply?.matched || []));
    result.missed.push(...(reply?.missed || []));
    if (reply?.error) {
      result.error = reply.error;
      break;
    }
    if (reply?.cancelled) {
      markCancelled(i, accountedCodes(reply));
      break;
    }
  }

  if (result.error || result.cancelled || retryUnattempted === false) return result;

  // Guarded re-send. The shipped extension's per-code fallback hard-codes a 1.5 s
  // dropdown wait (chrome-app/background.js) and we can't change it without a store
  // release, so a code it reports as NEVER ATTEMPTED gets one more solo request here
  // with the full template wait, readiness on its own row. Only never-clicked reasons
  // qualify: a second click on a code AdvancedMD already committed can DE-select it
  // (that is what commit_removed_existing_code reports), so anything that clicked —
  // even unsuccessfully — is left alone. An auto-mapped child is not re-sent either:
  // its parent was the attempted code.
  const selectorFor = new Map();
  const verifyFor = new Map();
  for (const e of entries) {
    (e.codes || []).forEach((code, i) => {
      selectorFor.set(code, e.optionSelectors && e.optionSelectors[i]);
      verifyFor.set(code, e.verifySelectors ? e.verifySelectors[i] : null);
    });
  }
  const candidates = result.missed.filter(
    (m) => m && !m.originalCode && RETRY_SAFE_MISS_REASONS.includes(m.reason) && selectorFor.has(m.code)
  );
  for (const miss of candidates) {
    if (cancelledNow()) {
      result.cancelled = true;
      break;
    }
    const code = miss.code;
    const verify = verifyFor.get(code);
    let reply;
    try {
      reply = await send({
        pasteString: code,
        codes: [code],
        optionSelectors: [selectorFor.get(code)],
        verifySelectors: verify != null ? [verify] : null,
      });
    } catch (e) {
      result.error = (e && e.message) || String(e);
      break;
    }
    const mine = (m) => m && (m.code === code || m.originalCode === code);
    const idx = result.missed.indexOf(miss);
    const nowMatched = (reply?.matched || []).find(mine);
    if (nowMatched) {
      result.missed.splice(idx, 1);
      result.matched.push({ ...nowMatched, retried: true });
    } else {
      const again = (reply?.missed || []).find(mine);
      result.missed.splice(idx, 1, { ...(again || miss), retried: true });
    }
    if (reply?.error) {
      result.error = reply.error;
      break;
    }
    if (reply?.cancelled) {
      result.cancelled = true;
      break;
    }
  }
  return result;
}

// Miss reasons the extension reports WITHOUT having clicked anything for that code.
// Anything else (a commit that timed out, a refinement that didn't commit, a verify
// miss, a removal) means a click happened and a re-send could de-select the diagnosis.
export const RETRY_SAFE_MISS_REASONS = [
  "no_option_found",
  "fallback_no_option_found",
  "fallback_init_failed",
  "fallback_no_picker_frame",
];
